/**
 * GroupLiveRoster — Live per-participant check-in view for the practitioner.
 *
 * VER-56 Model A: Shows who has joined, who is in-progress, who has submitted.
 * Simulates live updates via setInterval polling against mock data.
 *
 * ANONYMITY: Only opaque "Participant N" aliases are shown — never real names.
 * Individual WEMWBS scores are NEVER displayed here (only submitted/not status).
 *
 * MOCK-FIRST: No real WebSocket/SSE. A setInterval drives simulated progression
 * through the mock participant list to demonstrate live behaviour.
 *
 * TODOs:
 * - TODO [VER-55]: Replace the mock tick loop with a real-time transport
 *   (SSE preferred; WebSocket as fallback). Endpoint: GET /api/sessions/:id/events
 *   (server-sent events stream, event type "participant_update").
 * - TODO [RBAC]: Guard this view with the correct permit code once defined.
 * - TODO [DROP-REJOIN]: Participant drop/rejoin resilience is NOT implemented.
 *   A dropped participant should re-enter via the join screen; the backend must
 *   reuse the same participant slot (idempotent via session+alias key).
 *   Add reconnect indicator when transport is wired (serverless-reliability rule 5).
 * - TODO [VER-55]: "Close session" button should POST /api/sessions/:id/close
 *   which locks new joins and triggers aggregate computation.
 */

import React, { useEffect, useState, useRef } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from "@mui/material";
import {
    AccessTimeOutlined,
    CheckCircleOutlined,
    HourglassEmptyOutlined,
    StopCircleOutlined,
} from "@mui/icons-material";
import { VPill, VPillPositive, VPillAmber } from "../../../core/components/VPill";
import { ConfirmDialog } from "../../../core/components/ConfirmDialog";
import { GroupLiveSession, LiveParticipant, ParticipantCheckInStatus } from "./groupLive.mock";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type GroupLiveRosterProps = {
    session: GroupLiveSession;
    /** Called when the practitioner closes the session. Passes the closed session. */
    onSessionClosed: (session: GroupLiveSession) => void;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusLabel(status: ParticipantCheckInStatus): string {
    switch (status) {
        case "waiting":
            return "Waiting";
        case "in-progress":
            return "In progress";
        case "submitted":
            return "Submitted";
    }
}

const ParticipantStatusPill = ({ status }: { status: ParticipantCheckInStatus }): JSX.Element => {
    if (status === "submitted") {
        return <VPillPositive size="small" label={statusLabel(status)} />;
    }
    if (status === "in-progress") {
        return <VPill size="small" label={statusLabel(status)} color="primary" />;
    }
    return <VPillAmber size="small" label={statusLabel(status)} />;
};

const statusIcon = (status: ParticipantCheckInStatus): JSX.Element => {
    switch (status) {
        case "submitted":
            return <CheckCircleOutlined color="success" />;
        case "in-progress":
            return <AccessTimeOutlined color="primary" />;
        case "waiting":
            return <HourglassEmptyOutlined color="warning" />;
    }
};

function formatTime(iso: string | null): string {
    if (iso === null) {
        return "—";
    }
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

// ---------------------------------------------------------------------------
// Mock tick — simulates live participant progression (REMOVE when real SSE wired)
// ---------------------------------------------------------------------------

/**
 * Advances participant states through the mock lifecycle:
 * waiting → in-progress → submitted, one participant per tick.
 */
function advanceMockParticipants(participants: LiveParticipant[]): LiveParticipant[] {
    const now = new Date().toISOString();
    const nextWaiting = participants.find((p) => p.status === "waiting");
    const nextInProgress = participants.find((p) => p.status === "in-progress");

    return participants.map((p) => {
        if (nextInProgress && p.id === nextInProgress.id) {
            return { ...p, status: "submitted" as const, submittedAt: now };
        }
        if (!nextInProgress && nextWaiting && p.id === nextWaiting.id) {
            return { ...p, status: "in-progress" as const, joinedAt: p.joinedAt ?? now };
        }
        if (nextInProgress && nextWaiting && p.id === nextWaiting.id) {
            return { ...p, status: "in-progress" as const, joinedAt: p.joinedAt ?? now };
        }
        return p;
    });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GroupLiveRoster = ({ session, onSessionClosed }: GroupLiveRosterProps): JSX.Element => {
    const [participants, setParticipants] = useState<LiveParticipant[]>(session.participants);
    const [confirmClose, setConfirmClose] = useState<boolean>(false);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const submitted = participants.filter((p) => p.status === "submitted").length;
    const inProgress = participants.filter((p) => p.status === "in-progress").length;
    const waiting = participants.filter((p) => p.status === "waiting").length;
    const total = participants.length;
    const progressPct = total > 0 ? Math.round((submitted / total) * 100) : 0;
    const allSubmitted = submitted === total;

    // TODO [VER-55]: Replace this interval with real SSE event listener.
    useEffect(() => {
        if (allSubmitted || session.status !== "open") {
            return;
        }
        tickRef.current = setInterval(() => {
            setParticipants((prev) => {
                const updated = advanceMockParticipants(prev);
                // Stop ticking once all submitted
                const allDone = updated.every((p) => p.status === "submitted");
                if (allDone && tickRef.current !== null) {
                    clearInterval(tickRef.current);
                }
                return updated;
            });
        }, 3000);
        return () => {
            if (tickRef.current !== null) {
                clearInterval(tickRef.current);
            }
        };
        // Intentional empty dep array: this effect must only run once on mount.
        // allSubmitted and session.status are captured at mount time intentionally —
        // the mock tick manages its own termination via tickRef.
        // TODO [VER-55]: Remove this entire effect when real SSE is wired.
    }, []); // eslint-disable-line -- intentional mount-only effect (mock only)

    const handleCloseSession = () => {
        setConfirmClose(false);
        // TODO [VER-55]: POST /api/sessions/:id/close
        const closedSession: GroupLiveSession = {
            ...session,
            participants,
            status: "closed",
        };
        onSessionClosed(closedSession);
    };

    return (
        <Box p={4} width={1}>
            {/* Session header */}
            <Box mb={3} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4" color="textPrimary">
                        {session.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                        <Chip
                            size="small"
                            label={`Code: ${session.sessionCode}`}
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 700, letterSpacing: "0.15em" }}
                        />
                        <Chip size="small" label={`Started ${formatTime(session.startedAt)}`} variant="outlined" />
                        {session.status === "open" && <Chip size="small" label="Live" color="success" />}
                    </Box>
                </Box>

                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<StopCircleOutlined />}
                    onClick={() => setConfirmClose(true)}
                    disabled={session.status !== "open"}
                >
                    Close session
                </Button>
            </Box>

            {/* Progress summary */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="h6">
                            Submissions: {submitted} / {total}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {progressPct}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{ borderRadius: 2, height: 8, mb: 2 }}
                    />
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <CheckCircleOutlined color="success" sx={{ fontSize: "1rem" }} />
                            <Typography variant="body2">{submitted} submitted</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <AccessTimeOutlined color="primary" sx={{ fontSize: "1rem" }} />
                            <Typography variant="body2">{inProgress} in progress</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <HourglassEmptyOutlined color="warning" sx={{ fontSize: "1rem" }} />
                            <Typography variant="body2">{waiting} waiting</Typography>
                        </Box>
                    </Box>

                    {allSubmitted && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            All participants have submitted. You can close the session to view aggregate results.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Mock update notice */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Mock mode:</strong> Participant statuses are simulated. In production, updates arrive via
                server-sent events (TODO [VER-55]).
            </Alert>

            {/* Roster */}
            <Card>
                <CardContent>
                    <Typography variant="h6" mb={2}>
                        Participant roster
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <List dense disablePadding>
                        {participants.map((p, idx) => (
                            <React.Fragment key={p.id}>
                                <ListItem
                                    disableGutters
                                    sx={{ py: 1.5 }}
                                    secondaryAction={<ParticipantStatusPill status={p.status} />}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>{statusIcon(p.status)}</ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" fontWeight={700}>
                                                {p.alias}
                                            </Typography>
                                        }
                                        secondary={
                                            p.status === "submitted"
                                                ? `Submitted at ${formatTime(p.submittedAt)}`
                                                : p.joinedAt !== null
                                                  ? `Joined at ${formatTime(p.joinedAt)}`
                                                  : "Not yet joined"
                                        }
                                    />
                                </ListItem>
                                {idx < participants.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* Close session confirm dialog */}
            <ConfirmDialog
                open={confirmClose}
                title="Close session?"
                confirmButtonText="Close session"
                cancelButtonText="Keep open"
                onConfirm={handleCloseSession}
                onCancel={() => setConfirmClose(false)}
            >
                <Typography variant="body2">
                    Closing the session locks new joins and generates the anonymous group summary. Participants who have
                    not yet submitted will be marked as incomplete.{" "}
                    {waiting + inProgress > 0 && (
                        <strong>
                            {waiting + inProgress} participant{waiting + inProgress > 1 ? "s have" : " has"} not yet
                            submitted.
                        </strong>
                    )}
                </Typography>
            </ConfirmDialog>
        </Box>
    );
};

export default GroupLiveRoster;
